/**
 * New funnel's two ways in — Story 27.4.
 *
 * Visual Reference: `design/screens/funnels-list.html`, states `new-funnel`, `describe` and
 * `describe-unavailable`. One dialog whose contents are replaced, never a second stacked on it
 * (DESIGN-SYSTEM §4.17): **Start a new funnel** offers the templates and a description; **Describe
 * your business** asks the two questions and sends them to the editor's drafting state, where the
 * proposal is made. Nothing is created here — the funnel exists only once a proposal is accepted
 * (DECISION-167).
 *
 * WHETHER DRAFTING IS OPEN is read when the dialog opens (`GET …/proposals/allowance`), so a closed
 * door says why before anyone types. Only the day's limit is drawn; the kill switch, the month's
 * stop and drafting being down are described in the manifest and composed in the same place
 * (`proposal-copy.ts`). A read that fails leaves the row open: the draft itself says why, and
 * offers the templates, if it cannot be made.
 */
import { useEffect, useId, useState } from 'react'
import { readDescription } from '@leadgible/schema'
import { apiGet } from '../lib/api'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { COMPOSED, DRAWN } from '../editor/proposal-copy'
import { PROPOSAL_PATH, describedAs, keepRequest } from '../editor/proposal-request'

type Allowance = {
  closed: 'daily_limit' | 'switched_off' | 'budget_stop' | 'unavailable' | null
  drafted: number
  limit: number
  resets_at: number
}

/** Why the describe row is closed, in the words the dialog shows — or null while it is open. */
export function closedBecause(allowance: Allowance | null, org: string): string | null {
  switch (allowance?.closed ?? null) {
    case 'daily_limit':
      return DRAWN.pausedForTheDay(org, allowance!.limit)
    case 'switched_off':
      return COMPOSED.switchedOff(org)
    case 'budget_stop':
      return COMPOSED.budgetStop(org)
    case 'unavailable':
      return COMPOSED.unavailable
    default:
      return null
  }
}

export function NewFunnelDialog({
  workspaceId,
  org,
  opening,
  onClose,
}: {
  workspaceId: string
  /** The organisation's name — the drawn "Redwood Civil has drafted 5 funnels today". */
  org: string
  /** Which contents the dialog opens on: New funnel's choice, or the empty state's description. */
  opening: 'choose' | 'describe'
  onClose: () => void
}) {
  const [view, setView] = useState(opening)
  const [allowance, setAllowance] = useState<Allowance | null>(null)
  const [business, setBusiness] = useState('')
  const [customer, setCustomer] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const businessId = useId()
  const customerId = useId()
  const problemId = useId()

  useEffect(() => {
    let live = true
    void apiGet<{ allowance: Allowance }>(`/v1/w/${workspaceId}/proposals/allowance`)
      .then((answer) => {
        if (live) setAllowance(answer.allowance)
      })
      .catch(() => {
        // Left open: the draft says why, with the templates beside it, if it cannot be made.
      })
    return () => {
      live = false
    }
  }, [workspaceId])

  const closed = closedBecause(allowance, org)

  const draft = () => {
    const read = readDescription(describedAs(business, customer))
    if (!read.ok) {
      setProblem(read.reason)
      return
    }
    if (!keepRequest({ business: business.trim(), customer: customer.trim() })) {
      setProblem(COMPOSED.acceptFailed)
      return
    }
    window.location.assign(PROPOSAL_PATH)
  }

  if (view === 'describe') {
    return (
      <Modal
        title={DRAWN.describeTitle}
        balance
        closeMark={false}
        onClose={onClose}
        actions={
          <>
            <Button variant="secondary" onClick={() => setView('choose')}>
              {DRAWN.back}
            </Button>
            <Button onClick={draft}>{DRAWN.draftMyFunnel}</Button>
          </>
        }
      >
        <div className="flex flex-col gap-mdx-16">
          <Answer id={businessId} label={DRAWN.whatItDoes} value={business} problemId={problem ? problemId : null} onChange={setBusiness} />
          <div className="flex flex-col gap-mdx-6">
            <Answer id={customerId} label={DRAWN.whoFrom} value={customer} problemId={problem ? problemId : null} onChange={setCustomer} />
            <span className="text-pretty text-mdx-13 text-mdx-slate">{DRAWN.describeHelper}</span>
            {problem !== null ? (
              <span id={problemId} role="alert" className="text-pretty text-mdx-13 text-mdx-error">
                {problem}
              </span>
            ) : null}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      title={DRAWN.newFunnelTitle}
      balance
      closeMark={false}
      onClose={onClose}
      actions={
        <Button variant="secondary" onClick={onClose}>
          {DRAWN.cancel}
        </Button>
      }
    >
      <div className="flex flex-col gap-mdx-10">
        <Choice
          title={DRAWN.fromTemplate}
          line={DRAWN.fromTemplateLine}
          onChoose={() => window.location.assign('/library')}
        />
        <Choice
          title={DRAWN.describe}
          line={DRAWN.describeLine}
          closed={closed}
          onChoose={() => setView('describe')}
        />
      </div>
    </Modal>
  )
}

/** One of the drawn questions: its label over a three-row answer. */
function Answer({
  id,
  label,
  value,
  problemId,
  onChange,
}: {
  id: string
  label: string
  value: string
  problemId: string | null
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-mdx-6">
      <label htmlFor={id} className="text-mdx-14 font-mdx-semibold text-mdx-ink">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        aria-describedby={problemId ?? undefined}
        onChange={(event) => onChange(event.target.value)}
        className="w-full min-w-0 resize-y rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 leading-mdx-body text-mdx-ink outline-none focus:border-mdx-brand"
      />
    </div>
  )
}

/**
 * A choice row — the drawn 26px square, a title and its line. Closed, it is no control at all:
 * recessed, with the reason beneath, as `describe-unavailable` draws it.
 */
function Choice({
  title,
  line,
  closed = null,
  onChoose,
}: {
  title: string
  line: string
  closed?: string | null
  onChoose: () => void
}) {
  const body = (
    <>
      {/* The drawn 26px square is `--mdx-evidence-icon`'s — its own comment's "radius 8px" too. The
          export nudges it down 1px, which no token holds; it sits on the row's top edge instead. */}
      <span aria-hidden="true" className="size-mdx-evidence-icon flex-none rounded-mdx-control-sm bg-mdx-brand-tint" />
      <span className="flex min-w-0 flex-1 flex-col gap-mdx-2">
        <span className="text-mdx-15 font-mdx-semibold">{title}</span>
        <span className="text-pretty text-mdx-13 text-mdx-slate">{line}</span>
        {closed !== null ? <span className="mt-mdx-6 text-pretty text-mdx-13 text-mdx-slate">{closed}</span> : null}
      </span>
    </>
  )
  const frame = 'flex w-full items-start gap-mdx-14 rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-16 text-left text-mdx-ink'
  if (closed !== null) {
    return (
      <div aria-disabled="true" className={`${frame} cursor-not-allowed opacity-mdx-recessed`}>
        {body}
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onChoose}
      className={`${frame} cursor-pointer transition-colors ease-mdx-swift hover:bg-mdx-ground`}
    >
      {body}
    </button>
  )
}
